import {useCallback, useMemo} from 'react';
import {
  CheckoutData,
  useCheckoutMutation,
  useDeleteDiscountMutation,
  useGetCartQuery,
  useGetPackagesQuery,
  useLazyGetCartQuery,
  useLazyGetPackagesQuery,
  useSetDiscountMutation,
  useUpdateCartMutation,
} from '~/common/checkout/api/endpoints/cart';

const SET_DISCOUNT_FIXED_CACHE_KEY = 'shared-set-discount';
const DELETE_DISCOUNT_FIXED_CACHE_KEY = 'shared-delete-discount';
const UPDATE_CART_FIXED_CACHE_KEY = 'shared-update-cart';
const CHECKOUT_FIXED_CACHE_KEY = 'shared-checkout';

interface UseCartEndpointsProps {
  eventId?: string;
  skipCart?: boolean;
  skipPackages?: boolean;
  checkoutType?: CheckoutType;
}

export const useCartEndpoints = ({
  eventId = '',
  skipCart = false,
  skipPackages = false,
  checkoutType = 'event',
}: UseCartEndpointsProps) => {
  const sharedArgs = useMemo(
    () => ({
      eventId,
      apiVersion: 'v1',
      checkoutType,
    }),
    [checkoutType, eventId]
  );

  // queries & mutations
  const {data: cart, ...getCart} = useGetCartQuery(sharedArgs, {
    skip: skipCart || (!eventId && checkoutType === 'event'),
  });
  const transactionId = cart?.transactionId;
  const [refetchCartTrigger, refetchCart] = useLazyGetCartQuery();
  const {data: packages = [], ...getPackages} = useGetPackagesQuery(sharedArgs, {
    skip: !eventId || skipPackages,
  });
  const [refetchPackagesTrigger, refetchPackages] = useLazyGetPackagesQuery();
  const [deleteDiscountTrigger, deleteDiscount] = useDeleteDiscountMutation({
    fixedCacheKey: DELETE_DISCOUNT_FIXED_CACHE_KEY,
  });
  const [setDiscountTrigger, setDiscount] = useSetDiscountMutation({
    fixedCacheKey: SET_DISCOUNT_FIXED_CACHE_KEY,
  });
  const [updateCartTrigger, updateCart] = useUpdateCartMutation({
    fixedCacheKey: UPDATE_CART_FIXED_CACHE_KEY,
  });
  const [checkoutTrigger, checkout] = useCheckoutMutation({
    fixedCacheKey: CHECKOUT_FIXED_CACHE_KEY,
  });

  const selectedPackage = useMemo(() => {
    if (cart && 'packageId' in cart) {
      return (
        packages.find((p) => p.packageId === cart?.packageId) || packages.find((p) => p.default)
      );
    }
    return undefined;
  }, [cart, packages]);

  // status
  const isFetching = getCart.isFetching || getPackages.isFetching;
  const isLoading =
    getCart.isLoading ||
    getPackages.isLoading ||
    setDiscount.isLoading ||
    deleteDiscount.isLoading ||
    updateCart.isLoading ||
    checkout.isLoading ||
    refetchCart.isLoading ||
    refetchPackages.isLoading;

  // api wrapper functions
  const handleRefetchCart = useCallback(
    async () => refetchCartTrigger(sharedArgs),
    [refetchCartTrigger, sharedArgs]
  );
  const handleRefetchPackages = useCallback(
    async () => refetchPackagesTrigger(sharedArgs),
    [refetchPackagesTrigger, sharedArgs]
  );
  const handleDeleteDiscount = useCallback(
    async () => deleteDiscountTrigger({...sharedArgs, transactionId}),
    [deleteDiscountTrigger, sharedArgs, transactionId]
  );
  const handleSetDiscount = useCallback(
    async (code: string) => setDiscountTrigger({...sharedArgs, transactionId, data: {code}}),
    [setDiscountTrigger, sharedArgs, transactionId]
  );
  const handleUpdateCart = useCallback(
    async (data: Partial<Cart>) => updateCartTrigger({...sharedArgs, transactionId, data}),
    [sharedArgs, transactionId, updateCartTrigger]
  );
  const handleCheckout = useCallback(
    async (data: CheckoutData) => checkoutTrigger({...sharedArgs, transactionId, data}),
    [checkoutTrigger, sharedArgs, transactionId]
  );

  return {
    // data
    cart,
    packages,
    selectedPackage,
    checkoutData: checkout.data,
    // combined statuses
    isFetching,
    isLoading,
    // triggers
    deleteDiscount: handleDeleteDiscount,
    setDiscount: handleSetDiscount,
    updateCart: handleUpdateCart,
    checkout: handleCheckout,
    refetchCart: handleRefetchCart,
    refetchPackages: handleRefetchPackages,
    // statuses
    status: {
      getCart,
      getPackages,
      deleteDiscount,
      setDiscount,
      updateCart,
      checkout,
      refetchCart,
      refetchPackages,
    },
  };
};
